import type { Project, Template, FileInfo, BuildResult, CreateProjectRequest, MarketplaceTemplate, Registry, DeploymentOption, GeneratedFile, LibraryCopyRecord, ProjectLink, ProjectHierarchy, CopyLibrariesResponse, CreateLinkRequest, TestRun, TestConfig, TestTrend, FlakyTest, BuildRun, BuildConfig } from '../types'
import { fetchAPI, API_BASE } from './core'

// Projects
export async function listProjects(filter?: { status?: string; language?: string; type?: string; org_id?: string }): Promise<{ projects: Project[]; count: number }> {
  const params = new URLSearchParams()
  if (filter?.status) params.set('status', filter.status)
  if (filter?.language) params.set('language', filter.language)
  if (filter?.type) params.set('type', filter.type)
  if (filter?.org_id) params.set('org_id', filter.org_id)

  const query = params.toString()
  return fetchAPI(`/projects${query ? `?${query}` : ''}`)
}

export async function getProject(id: string): Promise<Project> {
  return fetchAPI(`/projects/${id}`)
}

export async function createProject(data: CreateProjectRequest): Promise<Project> {
  return fetchAPI('/projects', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function deleteProject(id: string): Promise<{ message: string; id: string }> {
  return fetchAPI(`/projects/${id}`, {
    method: 'DELETE',
  })
}

// Factory-owned project version
export async function getProjectVersion(id: string): Promise<{ version: string }> {
  return fetchAPI(`/projects/${id}/version`)
}

// Set the version to an explicit value (any direction, including resetting it downward).
export async function setProjectVersion(id: string, version: string): Promise<{ version: string }> {
  return fetchAPI(`/projects/${id}/version`, {
    method: 'PUT',
    body: JSON.stringify({ version }),
  })
}

// Step the version by bump type.
export async function bumpProjectVersion(id: string, bump: 'patch' | 'minor' | 'major'): Promise<{ version: string }> {
  return fetchAPI(`/projects/${id}/version`, {
    method: 'PUT',
    body: JSON.stringify({ bump }),
  })
}

export async function updateProject(id: string, updates: { name?: string; type?: string }): Promise<{ message: string; id: string; name: string }> {
  return fetchAPI(`/projects/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  })
}

// Claim an anonymous project after registration/login.
// Transfers ownership from _anonymous to the authenticated user.
export async function claimProject(id: string): Promise<{ message: string; project_id: string }> {
  return fetchAPI(`/projects/${id}/claim`, {
    method: 'POST',
  })
}

export async function getProjectFiles(id: string, path?: string): Promise<{ project_id: string; current_path: string; files: FileInfo[]; count: number }> {
  const params = path ? `?path=${encodeURIComponent(path)}` : ''
  return fetchAPI(`/projects/${id}/files${params}`)
}

export async function getProjectStatus(id: string): Promise<{ project_id: string; status: string; name: string; language: string; type: string; path: string }> {
  return fetchAPI(`/projects/${id}/status`)
}

export async function getFileContent(projectId: string, filePath: string): Promise<{ content: string; path: string; size: number }> {
  return fetchAPI(`/projects/${projectId}/file?path=${encodeURIComponent(filePath)}`)
}

export async function saveFileContent(projectId: string, filePath: string, content: string): Promise<{ message: string; project_id: string; path: string; size: number }> {
  return fetchAPI(`/projects/${projectId}/file?path=${encodeURIComponent(filePath)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'text/plain' },
    body: content,
  })
}

export async function createFile(projectId: string, filePath: string, content?: string): Promise<{ message: string; project_id: string; path: string }> {
  return fetchAPI(`/projects/${projectId}/file?path=${encodeURIComponent(filePath)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain' },
    body: content || '',
  })
}

export async function deleteFile(projectId: string, filePath: string): Promise<{ message: string }> {
  return fetchAPI(`/projects/${projectId}/file?path=${encodeURIComponent(filePath)}`, {
    method: 'DELETE',
  })
}

export async function renameFile(projectId: string, from: string, to: string): Promise<{ message: string; from: string; to: string }> {
  return fetchAPI(`/projects/${projectId}/file/rename`, {
    method: 'POST',
    body: JSON.stringify({ from, to }),
  })
}

export interface FileEdit {
  id: string
  project_id: string
  file_path: string
  edited_by: string
  size_bytes: number
  edited_at: string
}

export async function getFileEditHistory(projectId: string): Promise<{ edits: FileEdit[] }> {
  return fetchAPI(`/projects/${projectId}/file/history`)
}

// Templates
export async function listTemplates(): Promise<{ templates: Template[]; count: number }> {
  return fetchAPI('/templates')
}

export async function getTemplate(id: string): Promise<Template> {
  return fetchAPI(`/templates/${id}`)
}

export async function createTemplate(data: {
  name: string
  language: string
  type: string
  description?: string
  variables?: { name: string; description: string; required: boolean; default?: string }[]
  files: { path: string; content: string; executable: boolean }[]
  post_init?: string[]
}): Promise<Template> {
  return fetchAPI('/templates', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function deleteTemplate(id: string): Promise<{ message: string }> {
  return fetchAPI(`/templates/${id}`, {
    method: 'DELETE',
  })
}

// Build
export async function triggerBuild(projectId: string): Promise<BuildResult> {
  return fetchAPI(`/build/${projectId}`, {
    method: 'POST',
  })
}

export async function getBuildStatus(projectId: string): Promise<{ project_id: string; status: string }> {
  return fetchAPI(`/build/${projectId}/status`)
}

// Dev Server
export interface DevServerStatus {
  project_id: string
  port?: number
  running: boolean
  output?: string[]
  message?: string
}

export async function startDevServer(projectId: string, port: number = 3000): Promise<DevServerStatus> {
  return fetchAPI(`/projects/${projectId}/dev-server/start`, {
    method: 'POST',
    body: JSON.stringify({ port }),
  })
}

export async function stopDevServer(projectId: string): Promise<{ project_id: string; message: string }> {
  return fetchAPI(`/projects/${projectId}/dev-server/stop`, {
    method: 'POST',
  })
}

export async function getDevServerStatus(projectId: string): Promise<DevServerStatus> {
  return fetchAPI(`/projects/${projectId}/dev-server/status`)
}

// Marketplace
export async function listMarketplaceTemplates(filter?: { q?: string; language?: string; type?: string }): Promise<{ templates: MarketplaceTemplate[]; count: number }> {
  const params = new URLSearchParams()
  if (filter?.q) params.set('q', filter.q)
  if (filter?.language) params.set('language', filter.language)
  if (filter?.type) params.set('type', filter.type)

  const query = params.toString()
  return fetchAPI(`/marketplace/templates${query ? `?${query}` : ''}`)
}

export async function getMarketplaceTemplate(id: string): Promise<MarketplaceTemplate> {
  return fetchAPI(`/marketplace/templates/${id}`)
}

export async function installMarketplaceTemplate(id: string): Promise<{ message: string; id: string; path: string }> {
  return fetchAPI(`/marketplace/templates/${id}/install`, {
    method: 'POST',
  })
}

export async function getInstalledTemplates(): Promise<{ templates: MarketplaceTemplate[]; count: number }> {
  return fetchAPI('/marketplace/installed')
}

export async function exportTemplate(path: string): Promise<{ data: string; filename: string; size: number }> {
  return fetchAPI('/marketplace/export', {
    method: 'POST',
    body: JSON.stringify({ path }),
  })
}

export async function importTemplate(data: string, language: string, type: string, name: string): Promise<{ message: string; path: string }> {
  return fetchAPI('/marketplace/import', {
    method: 'POST',
    body: JSON.stringify({ data, language, type, name }),
  })
}

export async function listRegistries(): Promise<{ registries: Registry[]; count: number }> {
  return fetchAPI('/marketplace/registries')
}

export async function addRegistry(name: string, url: string): Promise<{ message: string; name: string }> {
  return fetchAPI('/marketplace/registries', {
    method: 'POST',
    body: JSON.stringify({ name, url }),
  })
}

export async function removeRegistry(name: string): Promise<{ message: string; name: string }> {
  return fetchAPI(`/marketplace/registries/${name}`, {
    method: 'DELETE',
  })
}

// Deployment
export async function getDeploymentOptions(projectId: string): Promise<{ project_id: string; language: string; options: DeploymentOption[] }> {
  return fetchAPI(`/deploy/options/${projectId}`)
}

export async function previewDeployment(projectId: string, type: string): Promise<{ project_id: string; type: string; files: GeneratedFile[] }> {
  return fetchAPI(`/deploy/preview/${projectId}?type=${type}`)
}

export async function generateDeployment(
  projectId: string,
  type: string,
  port?: number,
  envVars?: Record<string, string>,
  write?: boolean
): Promise<{ project_id: string; type: string; files: GeneratedFile[]; written: boolean }> {
  return fetchAPI(`/deploy/generate/${projectId}`, {
    method: 'POST',
    body: JSON.stringify({ type, port, env_vars: envVars, write }),
  })
}

export async function getDeploymentStatus(projectId: string): Promise<{
  project_id: string
  existing_files: string[]
  has_dockerfile: boolean
  has_ci: boolean
}> {
  return fetchAPI(`/deploy/status/${projectId}`)
}

// Project Linking
export async function createProjectLink(projectId: string, data: CreateLinkRequest): Promise<ProjectLink> {
  return fetchAPI(`/projects/${projectId}/links`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function getProjectHierarchy(projectId: string): Promise<ProjectHierarchy> {
  return fetchAPI(`/projects/${projectId}/links`)
}

export async function getProjectLinks(projectId: string): Promise<{ project_id: string; links: ProjectLink[]; count: number }> {
  return fetchAPI(`/projects/${projectId}/links/all`)
}

export async function deleteProjectLink(projectId: string, linkId: string): Promise<{ message: string }> {
  return fetchAPI(`/projects/${projectId}/links/${linkId}`, {
    method: 'DELETE',
  })
}

export async function getProjectLibraries(projectId: string): Promise<{ project_id: string; libraries: Project[]; count: number }> {
  return fetchAPI(`/projects/${projectId}/libraries`)
}

export async function getProjectSubprojects(projectId: string): Promise<{ project_id: string; subprojects: Project[]; count: number }> {
  return fetchAPI(`/projects/${projectId}/subprojects`)
}

export async function getProjectDependents(projectId: string): Promise<{ project_id: string; dependents: Project[]; count: number }> {
  return fetchAPI(`/projects/${projectId}/dependents`)
}

export async function copyLibraries(projectId: string): Promise<CopyLibrariesResponse> {
  return fetchAPI(`/projects/${projectId}/libraries/copy`, {
    method: 'POST',
  })
}

export async function getCopyRecords(projectId: string): Promise<{ project_id: string; copy_records: LibraryCopyRecord[]; count: number }> {
  return fetchAPI(`/projects/${projectId}/libraries/copies`)
}

export async function cleanLibraryCopies(projectId: string): Promise<{ project_id: string; message: string }> {
  return fetchAPI(`/projects/${projectId}/libraries/copies`, {
    method: 'DELETE',
  })
}

// Legacy project-scoped execution API (for PRDPanel compatibility)
export interface LegacyStartExecutionRequest {
  prd_id: string
  agent_count?: number
}

export interface LegacyStartExecutionResponse {
  execution_id: string
  prd_id: string
  agent_count: number
  status: string
}

export async function startProjectExecution(projectId: string, data: LegacyStartExecutionRequest): Promise<LegacyStartExecutionResponse> {
  return fetchAPI(`/projects/${projectId}/execution/start`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function stopProjectExecution(projectId: string, prdId: string): Promise<{ status: string; prd_id: string }> {
  return fetchAPI(`/projects/${projectId}/execution/stop?prd_id=${prdId}`, {
    method: 'POST',
  })
}

// Project preview session. The server returns the URL to load: a dedicated
// per-project preview origin with a short-lived signed link when configured,
// otherwise the same-origin preview path. Never put the access token in it —
// the previewed app can read its own URL.
export interface PreviewSession {
  url: string
  cross_origin: boolean
}

export async function createPreviewSession(projectId: string): Promise<PreviewSession> {
  const session: PreviewSession = await fetchAPI(`/projects/${projectId}/preview-session`, { method: 'POST' })
  // Same-origin paths come back rooted at /api; honor a non-default API base.
  if (!session.cross_origin && session.url.startsWith('/api/')) {
    session.url = `${API_BASE}${session.url.slice('/api'.length)}`
  }
  return session
}

// Test Runner
export async function startTestRun(projectId: string): Promise<TestRun> {
  return fetchAPI(`/projects/${projectId}/tests/run`, { method: 'POST' })
}

export async function listTestRuns(projectId: string): Promise<TestRun[]> {
  return fetchAPI(`/projects/${projectId}/tests/runs`)
}

export async function getTestRun(runId: string): Promise<TestRun> {
  return fetchAPI(`/tests/runs/${runId}`)
}

export async function cancelTestRun(runId: string): Promise<{ message: string }> {
  return fetchAPI(`/tests/runs/${runId}`, { method: 'DELETE' })
}

export async function getTestConfig(projectId: string): Promise<TestConfig | null> {
  try {
    return await fetchAPI<TestConfig>(`/projects/${projectId}/tests/config`)
  } catch {
    return null
  }
}

export async function getTestTrends(projectId: string, days = 30): Promise<TestTrend[]> {
  return fetchAPI<TestTrend[]>(`/projects/${projectId}/tests/trends?days=${days}`)
}

export async function getFlakyTests(projectId: string, limit = 20): Promise<FlakyTest[]> {
  return fetchAPI<FlakyTest[]>(`/projects/${projectId}/tests/flaky?limit=${limit}`)
}

// Build Runner API
export async function startBuildRun(projectId: string): Promise<BuildRun> {
  return fetchAPI(`/projects/${projectId}/builds/run`, { method: 'POST' })
}

export async function listBuildRuns(projectId: string, limit = 20): Promise<BuildRun[]> {
  return fetchAPI(`/projects/${projectId}/builds/runs?limit=${limit}`)
}

export async function getBuildRun(runId: string): Promise<BuildRun> {
  return fetchAPI(`/builds/runs/${runId}`)
}

export async function cancelBuildRun(runId: string): Promise<{ message: string }> {
  return fetchAPI(`/builds/runs/${runId}`, { method: 'DELETE' })
}

export async function getBuildConfig(projectId: string): Promise<BuildConfig | null> {
  try {
    return await fetchAPI<BuildConfig>(`/projects/${projectId}/builds/config`)
  } catch {
    return null
  }
}

// Git Operations
export async function getProjectGitStatus(projectId: string): Promise<import('../types').GitStatus> {
  return fetchAPI(`/projects/${projectId}/git`)
}

export async function getProjectGitFiles(projectId: string, branch?: string): Promise<{ files: import('../types').GitFileChange[]; branch?: string; base?: string; factory_exists?: boolean }> {
  const params = branch ? `?branch=${branch}` : ''
  return fetchAPI(`/projects/${projectId}/git/files${params}`)
}

export async function getProjectGitDiff(projectId: string, path: string, branch?: string): Promise<{ diff: import('../types').FileDiff | null }> {
  const params = new URLSearchParams({ path })
  if (branch) params.set('branch', branch)
  return fetchAPI(`/projects/${projectId}/git/diff?${params}`)
}

export async function getProjectGitCommits(projectId: string, limit?: number): Promise<{ commits: import('../types').GitCommit[] }> {
  const params = limit ? `?limit=${limit}` : ''
  return fetchAPI(`/projects/${projectId}/git/commits${params}`)
}

export async function connectProjectGit(projectId: string, remoteUrl: string, token?: string): Promise<{ message: string }> {
  return fetchAPI(`/projects/${projectId}/git/connect`, {
    method: 'POST',
    body: JSON.stringify({ remote_url: remoteUrl, token }),
  })
}

export async function disconnectProjectGit(projectId: string): Promise<{ message: string }> {
  return fetchAPI(`/projects/${projectId}/git/disconnect`, { method: 'POST' })
}

export async function pushProjectGit(projectId: string, force?: boolean, commitMessage?: string, branch?: string, toBranch?: string): Promise<{ message: string; branch: string; remote_branch?: string; committed: boolean }> {
  return fetchAPI(`/projects/${projectId}/git/push`, {
    method: 'POST',
    body: JSON.stringify({ force, commit_message: commitMessage, branch, to_branch: toBranch }),
  })
}

// keepConflicts leaves a conflicted merge in progress (409 lists `conflicts`)
// so it can be finished with resolveGitConflict/completeGitMerge or dropped
// with abortGitMerge. On the factory branch the pull also merges origin/factory
// when the remote has commits this checkout lacks.
export async function pullProjectGit(projectId: string, strategy?: 'merge' | 'force', keepConflicts?: boolean): Promise<{ message: string; strategy?: string }> {
  const body: Record<string, unknown> = {}
  if (strategy) body.strategy = strategy
  if (keepConflicts) body.keep_conflicts = true
  return fetchAPI(`/projects/${projectId}/git/pull`, {
    method: 'POST',
    body: Object.keys(body).length ? JSON.stringify(body) : undefined,
  })
}

export async function resolveGitConflict(projectId: string, path: string, resolution: 'ours' | 'theirs' | 'manual'): Promise<{ message: string; remaining: string[] }> {
  return fetchAPI(`/projects/${projectId}/git/merge/resolve`, {
    method: 'POST',
    body: JSON.stringify({ path, resolution }),
  })
}

export async function completeGitMerge(projectId: string): Promise<{ message: string; branch: string }> {
  return fetchAPI(`/projects/${projectId}/git/merge/complete`, { method: 'POST' })
}

export async function abortGitMerge(projectId: string): Promise<{ message: string }> {
  return fetchAPI(`/projects/${projectId}/git/merge/abort`, { method: 'POST' })
}

export async function fetchProjectGit(projectId: string): Promise<{ message: string }> {
  return fetchAPI(`/projects/${projectId}/git/fetch`, { method: 'POST' })
}

export interface BranchInfo {
  name: string
  remote: boolean
  current: boolean
}

export async function listProjectBranches(projectId: string): Promise<{ branches: BranchInfo[]; current: string }> {
  return fetchAPI(`/projects/${projectId}/git/branches`)
}

export async function checkoutBranch(projectId: string, branch: string): Promise<{ message: string; branch: string }> {
  return fetchAPI(`/projects/${projectId}/git/checkout`, {
    method: 'POST',
    body: JSON.stringify({ branch }),
  })
}

export async function createBranch(projectId: string, branch: string, from?: string, checkout?: boolean): Promise<{ message: string; branch: string }> {
  return fetchAPI(`/projects/${projectId}/git/branches`, {
    method: 'POST',
    body: JSON.stringify({ branch, from, checkout }),
  })
}

export async function deleteBranch(projectId: string, branch: string): Promise<{ message: string }> {
  return fetchAPI(`/projects/${projectId}/git/branches?branch=${encodeURIComponent(branch)}`, {
    method: 'DELETE',
  })
}

export async function initAndPushProject(projectId: string, remoteUrl: string, branch?: string): Promise<{
  message: string
  branch: string
  remote: string
  owner: string
  repo: string
}> {
  return fetchAPI(`/projects/${projectId}/git/init-push`, {
    method: 'POST',
    body: JSON.stringify({ remote_url: remoteUrl, branch }),
  })
}

export async function reconnectProjectGit(projectId: string): Promise<{
  message: string
  remote_url: string
  branch: string
  repo_owner: string
  repo_name: string
}> {
  return fetchAPI(`/projects/${projectId}/git/reconnect`, {
    method: 'POST',
  })
}

export async function mergeFactoryToMain(projectId: string): Promise<{
  message: string
  base_branch: string
  details?: string
}> {
  return fetchAPI(`/projects/${projectId}/git/merge-factory`, {
    method: 'POST',
  })
}

export async function syncMainToFactory(projectId: string): Promise<{
  message: string
  base_branch: string
  synced: boolean
  details?: string
}> {
  return fetchAPI(`/projects/${projectId}/git/sync-factory`, {
    method: 'POST',
  })
}

export async function createGitHubRepo(name: string, description?: string, isPrivate?: boolean): Promise<{
  full_name: string
  name: string
  html_url: string
  clone_url: string
  private: boolean
  owner: string
}> {
  return fetchAPI('/github/repos', {
    method: 'POST',
    body: JSON.stringify({ name, description, private: isPrivate }),
  })
}

// GitHub Import
export interface GitHubRepo {
  full_name: string
  name: string
  owner: string
  description: string
  language: string
  default_branch: string
  private: boolean
  html_url: string
  clone_url: string
  updated_at: string
  stargazers_count: number
}

export interface ImportProjectRequest {
  repo_url: string
  name?: string
  language?: string
  type?: string
  org_id?: string
}

export async function startGitHubDeviceFlow(): Promise<{
  device_code: string
  user_code: string
  verification_uri: string
  expires_in: number
  interval: number
}> {
  return fetchAPI('/github/device/start', { method: 'POST' })
}

export async function pollGitHubDeviceFlow(deviceCode: string): Promise<{
  status: string
  access_token?: string
  username?: string
  error?: string
}> {
  return fetchAPI(`/github/device/poll?device_code=${encodeURIComponent(deviceCode)}`)
}

export async function getGitHubAuthStatus(): Promise<{
  authenticated: boolean
  username?: string
  error?: string
}> {
  return fetchAPI('/github/auth/status')
}

export async function logoutGitHub(): Promise<{ message: string }> {
  return fetchAPI('/github/auth/logout', { method: 'POST' })
}

export async function listGitHubRepos(
  page?: number,
  perPage?: number,
  search?: string
): Promise<{ repos: GitHubRepo[]; page: number; per_page: number }> {
  const params = new URLSearchParams()
  if (page) params.set('page', page.toString())
  if (perPage) params.set('per_page', perPage.toString())
  if (search) params.set('search', search)
  const query = params.toString()
  return fetchAPI(`/github/repos${query ? `?${query}` : ''}`)
}

export async function importProject(data: ImportProjectRequest): Promise<{
  id: string
  name: string
  path: string
  language: string
  type: string
  status: string
}> {
  return fetchAPI('/projects/import', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

// Cost Estimation API
export interface CostLineItem {
  resource: string
  description: string
  quantity: number
  unit_price: number
  unit: string
  monthly: number
}

export interface CostEstimate {
  provider: string
  monthly_total: number
  hourly_total: number
  breakdown: CostLineItem[]
  assumptions: string[]
  tier: string
  region: string
}

export interface CloudProvider {
  id: string
  name: string
  description: string
  type: string
}

export interface CostTier {
  id: string
  name: string
  description: string
  typical_use: string
}

export async function listCostProviders(): Promise<{ providers: CloudProvider[] }> {
  return fetchAPI('/cost/providers')
}

export async function listCostTiers(): Promise<{ tiers: CostTier[] }> {
  return fetchAPI('/cost/tiers')
}

export async function compareCostProviders(projectId: string, options?: { tier?: string; database?: boolean; storage?: boolean }): Promise<{ estimates: CostEstimate[]; cheapest: CostEstimate | null }> {
  const params = new URLSearchParams()
  if (options?.tier) params.set('tier', options.tier)
  if (options?.database !== undefined) params.set('database', String(options.database))
  if (options?.storage !== undefined) params.set('storage', String(options.storage))
  const query = params.toString()
  return fetchAPI(`/cost/compare/${projectId}${query ? `?${query}` : ''}`)
}

export async function estimateProjectCost(projectId: string, data: { provider: string; tier: string; database?: boolean; storage?: boolean }): Promise<CostEstimate> {
  return fetchAPI(`/cost/estimate/${projectId}`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

// Code Metrics API
export interface LintIssue {
  file: string
  line: number
  column?: number
  severity: string
  message: string
  rule?: string
}

export interface FunctionMetric {
  name: string
  file: string
  line: number
  complexity: number
}

export interface ComplexityReport {
  average: number
  max: number
  high_complexity: FunctionMetric[]
}

export interface CodeMetrics {
  language: string
  lines_of_code: number
  files: number
  functions: number
  test_coverage: number
  lint_issues: LintIssue[]
  complexity: ComplexityReport
  dependencies: number
  test_files: number
}

export async function getProjectMetrics(projectId: string): Promise<{ metrics: CodeMetrics }> {
  return fetchAPI(`/metrics/${projectId}`)
}
